import React, { useState } from 'react';
import { Box } from '@mui/material';
import { DEFAULT_ERROR_MESSAGE, MESSAGE_MODAL_FORM_CONTEXT, ModalForm } from 'framework/form';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import { VendorDto } from '../../types';
import { useShowContext } from 'framework/show';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { useListContext } from 'framework/list';
import { Tooltip } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';

interface AgreementActionButtonProps {
  vendorData?: VendorDto;
  icon: React.ReactElement;
  color: 'success' | 'error';
  formTitle: string;
  tooltip: string;
  canPerformAction: boolean;
  onSubmit: (agreementId: string, values: any) => Promise<any>;
  getValidationSchema: () => Promise<any>;
  initialData?: any;
  children: React.ReactNode;
}

const AgreementActionButton = ({
  icon,
  color,
  formTitle,
  tooltip,
  canPerformAction,
  onSubmit,
  getValidationSchema,
  initialData = {},
  children,
}: AgreementActionButtonProps) => {
  const { data: agreement } = useShowContext();
  const { addMessage, cleanMessage } = useMessageService(MESSAGE_MODAL_FORM_CONTEXT);
  const { reloadData = () => {} } = useListContext();
  const [modalOpen, setModalOpen] = useState(false);

  const agreementId = agreement?.id;

  const handleSubmit = async (values: FormFieldsData) => {
    try {
      const result = await onSubmit(agreementId, values);
      return result || {};
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE;
      addMessage(new ErrorMessage(errorMessage));
      return false;
    }
  };

  const openModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    cleanMessage();
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  if (!canPerformAction) {
    return null;
  }

  return (
    <>
      <Tooltip title={tooltip}>
        <Box
          component="button"
          type="button"
          onClick={openModal}
          sx={{
            p: 0,
            m: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: `${color}.main`,
            '&:hover': { color: `${color}.dark` },
          }}
        >
          {icon}
        </Box>
      </Tooltip>
      <Modal
        backdrop="static"
        isOpen={modalOpen}
        title={formTitle}
        onToggle={closeModal}
        hasCancelButton
        className="form-modal"
        modalSize="smallMobile"
        paperStyles={{ background: 'var(--mui-palette-background-paper)' }}
      >
        <ModalForm
          getValidationSchemaFunc={getValidationSchema}
          modalOpen={modalOpen}
          closeModal={closeModal}
          handleDirty
          submitFormFunc={handleSubmit}
          afterSubmit={{
            execute: () => reloadData(),
          }}
          initialData={initialData}
          isHideDefaultButtons={false}
          getDataFunc={undefined}
          isCloseAfterSubmit
        >
          {children}
        </ModalForm>
      </Modal>
    </>
  );
};

export default AgreementActionButton;
